import { useEffect, useRef, useState } from 'react';
import { useTab } from '@react-aria/tabs';
import { useFocusRing } from '@react-aria/focus';

// styles
import { TabListState } from '@react-stately/tabs';
import styles from './sliding-tabs.module.scss';

// types
import type { Node } from '@react-types/shared';

// utils
import { previewId } from 'utils/preview-id';
import { classNameBuilder } from 'utils/classNameBuilder';

type TabProps = {
  item: Node<Tab>;
  state: TabListState<Tab>;
  onScroll: (x: number) => void;
  tabIndex: number;
};

export function Tab({ item, state, tabIndex, onScroll }: Readonly<TabProps>) {
  const [myID, setMyID] = useState<string>('');
  const [myAriaControl, setMyAriaControl] = useState<string>('');

  const { key, rendered } = item;
  const ref = useRef<HTMLDivElement>(null);
  const { tabProps } = useTab({ key }, state, ref);
  const isSelected = state.selectedKey === key;
  const isDisabled = state.disabledKeys.has(key);
  const { isFocusVisible, focusProps } = useFocusRing();

  useEffect(() => {
    if (!tabProps?.id) return;
    setMyID(tabProps.id);
  }, []);

  useEffect(() => {
    if (!tabProps['aria-controls']) return;
    setMyAriaControl(tabProps['aria-controls']);
  }, []);

  useEffect(() => {
    if (isSelected) {
      ref.current?.scrollIntoView?.({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
      onScroll(ref.current?.parentElement?.scrollLeft || 0);
      state.selectionManager.setFocusedKey(key);
    }
  }, [isSelected]);

  return (
    <div
      {...tabProps}
      {...focusProps}
      ref={ref}
      className={classNameBuilder(
        isSelected && styles.active,
        isFocusVisible && styles.focusVisible,
      )}
      style={{
        opacity: isDisabled ? '0.5' : undefined,
      }}
      {...previewId(`#${tabIndex}`)}
      id={myID}
      aria-controls={myAriaControl}
    >
      {rendered}
    </div>
  );
}
